import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Alert, Anchor, Badge, Group, Stack, Text } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { Breadcrumbs, DetailShell, FilterChip, Skeleton, TabStrip } from '@lighthouse/design'
import { RestrictedState } from '../components/access/RestrictedState'
import { CartographerLink } from '../components/cartographer/CartographerLink'
import { cartographerReportPath } from '../utils/cartographerUrl'
import { AccessTab } from '../components/report-detail/AccessTab'
import { GovernanceTab } from '../components/report-detail/GovernanceTab'
import { HistoryTab } from '../components/report-detail/HistoryTab'
import { OperationsTab } from '../components/report-detail/OperationsTab'
import { OverviewTab, TabFailure } from '../components/report-detail/OverviewTab'
import { SchedulingTab } from '../components/report-detail/SchedulingTab'
import { UsageTab } from '../components/report-detail/UsageTab'
import {
  REPORT_DETAIL_SURFACE, TAB_LABEL, isWindowedTab, windowChipLabel,
} from '../components/report-detail/reportDetailFormat'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { useReportDetailOverview } from '../hooks/useReportDetail'
import { errorKind, errorSummary } from '../utils/format'
import {
  REPORT_DETAIL_TABS, REPORT_DETAIL_WINDOWS, openReportDetailTab, parseReportDetailDays, reportDetailPath,
} from '../utils/reportDetailPath'
import type { ReportDetailTab, ReportDetailWindow } from '../utils/reportDetailPath'

const TABS = REPORT_DETAIL_TABS.map((value) => ({ value, label: TAB_LABEL[value] }))

/**
 * Report Detail — `/registry/:reportId` — one report across seven READ-ONLY tabs:
 * Overview, Governance, Operations, Usage, Scheduling, Access and History. No
 * Lineage: it needs semantic models that do not exist.
 *
 * LighthouseAdmin, like every Observatory reader of Cartographer data. On a
 * `denied` gate it renders RestrictedState and fetches nothing; on `pending` (a
 * cold load, before the catalogue answers) a skeleton, and still nothing; on
 * `admin` and `unknown` each tab fetches its own read, and a 403 from it is the
 * restricted state.
 *
 * The tab and the window live in the URL (`?tab=`, `?days=`, read through
 * utils/reportDetailPath), so a shared link opens what the sender was looking
 * at. The header comes from the Overview read, which is therefore the one read
 * every tab keeps open. Each other tab mounts only while it is open and fails
 * alone — a tab whose read depends on an unapplied migration says so and leaves
 * the others standing.
 *
 * READ-ONLY OVER CARTOGRAPHER DATA, by ruling (D6a, 2026-09-30). There is no
 * reassign, revoke, keep, reverse or pause here and no control carries such a
 * verb: each tab shows the reason and hands off to Cartographer (`/governance`,
 * `/schedules`, `/archive`) through CartographerLink, and the header opens the
 * report itself there (`/reports/{id}`, W3-1).
 */
export function ReportDetailPage() {
  const { reportId = '' } = useParams<{ reportId: string }>()
  const gate = useLighthouseAdmin()

  if (gate.state === 'denied') {
    return (
      <Stack gap="md">
        <Crumbs current={reportId} />
        <RestrictedState surface={REPORT_DETAIL_SURFACE} reason={gate.reason} />
      </Stack>
    )
  }

  // Asked nothing yet, so it shows nothing yet.
  if (gate.state === 'pending') {
    return (
      <Stack gap="md" aria-busy="true" data-state="gate-pending">
        <Crumbs current={reportId} />
        <Skeleton height={120} />
        <Skeleton height={240} />
      </Stack>
    )
  }

  // Keyed on the report, so moving to another report remounts every tab rather than flashing the last one.
  return <ReportDetail key={reportId} reportId={reportId} />
}

function Crumbs({ current }: { current: string }) {
  const navigate = useNavigate()
  return (
    <Breadcrumbs
      items={[
        // The rail's own section for /registry (navModel.ts), as the app header's trail names it: the Reports
        // section went with the A6 prune, and a trail naming a section the rail lacks disagrees with the header.
        { label: 'Cartographer' },
        { label: 'Report Registry', onClick: () => navigate('/registry') },
        { label: current },
      ]}
    />
  )
}

function ReportDetail({ reportId }: { reportId: string }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tab = openReportDetailTab(searchParams.get('tab'))
  const days = parseReportDetailDays(searchParams.get('days'))

  // Replaced, not pushed: switching a tab or a window is not navigation worth a Back press.
  const go = (nextTab: ReportDetailTab, nextDays: ReportDetailWindow) =>
    navigate(reportDetailPath(reportId, nextTab, nextDays), { replace: true })

  const overview = useReportDetailOverview(reportId, { open: true })
  const overviewKind = overview.isError ? errorKind(overview.error) : null

  // Every tab would get the same answer: one restricted page, and one "No such report", not seven.
  if (overviewKind === 'forbidden' || overviewKind === 'not-found') {
    return (
      <Stack gap="md">
        <Crumbs current={reportId} />
        <TabFailure error={overview.error} what="this report" />
        {overviewKind === 'not-found' && (
          <Anchor component="button" type="button" size="sm" onClick={() => navigate('/registry')}>
            Back to the Report Registry
          </Anchor>
        )}
      </Stack>
    )
  }

  const report = overview.data?.report ?? null

  return (
    <Stack gap="md" pb="xl">
      <DetailShell
        breadcrumbs={<Crumbs current={report?.name ?? reportId} />}
        title={report ? report.name : overview.isError ? 'Report' : <Skeleton width={260} height={28} />}
        badges={report && (
          <Group gap={6}>
            <Badge variant="light" color="gray">{report.status}</Badge>
            {!report.isRunnable && <Badge variant="light" color="gray" tt="none">view-only</Badge>}
            {overview.data?.attentionRequired && <Badge variant="light" color="yellow" tt="none">attention required</Badge>}
          </Group>
        )}
        eyebrow={reportId}
        // The report itself, in Cartographer (W3-1): a link when this build knows where Cartographer is, the destination
        // named otherwise. Read-only like the rest of the page — opening a report changes nothing.
        actions={
          <CartographerLink path={cartographerReportPath(reportId)} label="This report">
            Open in Cartographer
          </CartographerLink>
        }
        tabs={<TabStrip tabs={TABS} value={tab} onChange={(next) => go(openReportDetailTab(next), days)} />}
      >
        <Stack gap="lg">
          {overview.isError && (
            <Alert variant="light" color="red" icon={<IconAlertCircle size={16} />} title="The report header could not be loaded">
              <Text size="sm">
                {errorSummary(overview.error) ?? 'The request failed.'} Each tab below loads on its own.
              </Text>
            </Alert>
          )}

          {isWindowedTab(tab) && (
            <Group gap={6} wrap="wrap" role="group" aria-label="Run-log window">
              {REPORT_DETAIL_WINDOWS.map((option) => (
                <FilterChip key={option} active={option === days} onClick={() => go(tab, option)}>
                  {windowChipLabel(option)}
                </FilterChip>
              ))}
              <Text size="xs" c="dimmed">Nothing longer: the run log keeps 90 days by default.</Text>
            </Group>
          )}

          {tab === 'overview' && <OverviewTab reportId={reportId} />}
          {tab === 'governance' && <GovernanceTab reportId={reportId} />}
          {tab === 'operations' && <OperationsTab reportId={reportId} days={days} />}
          {tab === 'usage' && <UsageTab reportId={reportId} days={days} report={report} />}
          {tab === 'scheduling' && <SchedulingTab reportId={reportId} />}
          {tab === 'access' && <AccessTab reportId={reportId} />}
          {tab === 'history' && <HistoryTab reportId={reportId} overview={overview.data ?? null} />}
        </Stack>
      </DetailShell>
    </Stack>
  )
}
